import { TextWithEllipsis, Typography } from 'ComponentLibrary/src/piazza/text'
import { SimpleTableCell } from './SimpleTableCell'
import {
    AreaValue,
    AreaValueDataObject,
    decimalToLocalizedString,
    isoDateStringToLocalFormat,
    joinDefinedStrings,
    monetaryToLocalizedString,
    MonetaryValueDataObjectType,
    tokens,
} from 'ComponentLibrary'
import { Box, Stack } from '@mui/material'
import { PercentageTrend } from 'ComponentLibrary/src/piazza/text/PercentageTrend/PercentageTrend'

export type SimpleTableTextProps = {
    variant?: 'headcolumn' | 'default' | 'lowContrast'
    text?: string
    unit?: string
    emphasis?: boolean
    alignment?: 'left' | 'right'
    highlight?: boolean
    selected?: boolean
    hoverable?: boolean
}

export function SimpleTableTextCell({
    variant = 'default',
    text,
    unit,
    emphasis,
    alignment,
    highlight,
    selected,
    hoverable,
}: SimpleTableTextProps) {
    const resolvedTextColor = highlight
        ? tokens.ColorTextWarningSubtle
        : variant === 'lowContrast'
          ? tokens.ColorTextSubtle
          : tokens.ColorTextBold
    const resolvedUnitColor = highlight ? tokens.ColorTextWarningSubtle : tokens.ColorTextSubtle
    return (
        <SimpleTableCell alignment={alignment} highlight={highlight} selected={selected} hoverable={hoverable}>
            <Stack
                direction="row"
                gap={tokens.SizingSpacing075}
                paddingLeft={variant === 'headcolumn' ? tokens.SizingSpacing100 : undefined}
                sx={{ minWidth: 0 }}
            >
                {text ? (
                    <>
                        <TextWithEllipsis
                            typographyProps={{
                                variant: 'textS',
                                bold: emphasis,
                                color: resolvedTextColor,
                            }}
                            tooltipText={text}
                        >
                            {text}
                        </TextWithEllipsis>
                        {unit && (
                            <Typography variant="textS" bold={emphasis} color={resolvedUnitColor}>
                                {unit}
                            </Typography>
                        )}
                    </>
                ) : (
                    <Typography variant="textS" bold={emphasis} color={resolvedTextColor}>
                        -
                    </Typography>
                )}
            </Stack>
        </SimpleTableCell>
    )
}

type SimpleTableNumberCellProps = {
    number?: number
    isInteger?: boolean
    unit?: string
    numberOfDigits?: number
    locale?: string
} & Omit<SimpleTableTextProps, 'text' | 'unit'>

export function SimpleTableNumberCell({ number, isInteger = false, unit, numberOfDigits, locale, ...rest }: SimpleTableNumberCellProps) {
    const text = number !== undefined ? decimalToLocalizedString(number, isInteger, numberOfDigits, numberOfDigits, locale) : undefined
    return <SimpleTableTextCell text={text} unit={unit} {...rest} />
}

type SimpleTableAreaCellProps = {
    area?: AreaValueDataObject
    locale?: string
} & Omit<SimpleTableTextProps, 'text' | 'unit'>

export function SimpleTableAreaCell({ area, locale, ...rest }: SimpleTableAreaCellProps) {
    const areaValue = area ? AreaValue.fromDataObject(area) : undefined
    const text = areaValue ? areaValue.format({ withUnit: false, minimumNumberOfDigits: 2, locale }) : undefined
    const unit = areaValue?.unitSymbol(locale)
    return <SimpleTableTextCell text={text} unit={unit} {...rest} />
}

type SimpleTableMonetaryCellProps = {
    monetaryValue?: MonetaryValueDataObjectType
    additionalUnit?: string
    rounded?: boolean
    locale?: string
} & Omit<SimpleTableTextProps, 'text' | 'unit'>

export function SimpleTableMonetaryCell({ monetaryValue, additionalUnit, rounded, locale, ...rest }: SimpleTableMonetaryCellProps) {
    const text = monetaryToLocalizedString(monetaryValue, false, rounded, locale) ?? undefined
    const unit = joinDefinedStrings([monetaryValue?.currency === 'EUR' ? '€' : monetaryValue?.currency || '€', additionalUnit], '')
    return <SimpleTableTextCell text={text} unit={unit} {...rest} />
}

type SimpleTableDateCellProps = {
    date?: string
    withTime?: boolean
    locale?: string
} & Omit<SimpleTableTextProps, 'text' | 'unit'>

export function SimpleTableDateCell({ date, withTime, locale, ...rest }: SimpleTableDateCellProps) {
    const text = isoDateStringToLocalFormat(date, !!withTime, locale)
    return <SimpleTableTextCell text={text} {...rest} />
}

type SimpleTablePercentageCellProps = {
    percentage?: number
    additionalUnit?: string
    numberOfDigits?: number
    locale?: string
} & Omit<SimpleTableTextProps, 'text' | 'unit'>

export function SimpleTablePercentageCell({ percentage, additionalUnit, numberOfDigits, locale, ...rest }: SimpleTablePercentageCellProps) {
    const text =
        percentage !== undefined
            ? decimalToLocalizedString(percentage * 100, false, numberOfDigits ?? 4, numberOfDigits, locale)
            : undefined
    const unit = joinDefinedStrings(['%', additionalUnit], ' ')
    return <SimpleTableTextCell text={text} unit={unit} {...rest} />
}

type SimpleTableTrendCellProps = {
    percentage?: number
    alignment?: 'left' | 'right'
    locale?: string
}

export function SimpleTableTrendCell({ percentage, alignment, locale }: SimpleTableTrendCellProps) {
    return (
        <Box bgcolor={percentage && percentage < 0 ? tokens.ColorSurfaceDangerDefault : undefined} width="100%">
            <SimpleTableCell alignment={alignment}>
                <PercentageTrend percentage={percentage} locale={locale} />
            </SimpleTableCell>
        </Box>
    )
}
